import React, {ReactElement, useEffect, useState} from "react";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {Box, Button, ButtonBase, CircularProgress, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledOrientationComponent} from "../shared/StyledComponents";
import {
  BORDER_RADIUS,
  DIVIDER,
  DIVIDER_XBLACK,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_LG_WIDTH,
  PAGE_FRAGMENT_SM_WIDTH,
  PD_LG,
  PD_MD,
  PD_XSM
} from "../shared/dimens";
import {AspectRatioOutlined, ColorLensOutlined} from "@mui/icons-material";
import {App} from "./App";
import {nightGray} from "../shared/colors";
import {bindPage} from "./camera";
import {$KTM, Action, KeyMetadatas, Orientation} from "../shared/types";

export enum BackgroundType {
  COLOR = "color",
  IMAGE = "image",
}

export type Background = {
  type: BackgroundType,
  color?: string,
  imageThumbnail?: string,
  imageUrl?: string,
}

const BACKGROUND_COLORS: string[] = [
  '#fa541c',
  '#faad14',
  '#fadb14',
  '#a0d911',
  '#52c41a',
  '#1890ff',
  '#2f54eb',
  '#722ed1',
  '#eb2f96',
  '#bfbfbf',
  '#a1a1a1',
  '#909090',
  '#606060',
  '#303030',
  '#101010',
];

export const BACKGROUNDS: Background[] = [
  ...Array(78).fill(0).map((_, index) => {
    return {
      type: BackgroundType.IMAGE,
      imageUrl: "/assets/puppets/backgrounds/images/bg" + (index + 1) + ".svg",
      imageThumbnail: "/assets/puppets/backgrounds/thumbs/bg" + (index + 1) + ".png",
    }
  }),
  ...BACKGROUND_COLORS.map((color) => {
    return {
      type: BackgroundType.COLOR,
      color: color,
    }
  }),
]

enum AspectRatio {
  WIDE = "wide",
  SQUARE = "square",
  TALL = "tall",
}

type AspectRatioMetadata = {
  text: string,
  value: number,
}

const ASPECT_RATIO_METADATAS = new KeyMetadatas<AspectRatioMetadata>([
  $KTM(AspectRatio.WIDE, {text: "Wide", value: 2}),
  $KTM(AspectRatio.SQUARE, {text: "Square", value: 1}),
  $KTM(AspectRatio.TALL, {text: "Tall", value: .5}),
]);

function BackgroundView(props: {
  initialBackground: Background,
  onChange: (background: Background) => void,
}): ReactElement {
  const [backgroundIndex, setBackgroundIndex] = useState<number>(BACKGROUNDS.findIndex(bg => bg === props.initialBackground));
  useEffect(() => {
    props.onChange(BACKGROUNDS[backgroundIndex]);
  }, [backgroundIndex]);
  return <StyledBoxColumn
    style={{width: PAGE_FRAGMENT_HALF_WIDTH, padding: PD_LG, height: PAGE_FRAGMENT_SM_WIDTH, overflowY: "scroll"}}>
    <Typography style={{fontWeight: "bold"}}>Background</Typography>
    <Grid container>
      {BACKGROUNDS.map((bg, index) => <Grid xs={4}><ButtonBase
        onClick={() => setBackgroundIndex(index)}
        style={{
          boxSizing: "border-box",
          border: index === backgroundIndex ? DIVIDER_XBLACK : null,
          width: "100%",
          aspectRatio: 2,
          padding: PD_XSM
        }}>
        {bg?.type === BackgroundType.IMAGE
          ? <img src={bg.imageThumbnail} style={{width: "100%", height: "100%", objectFit: "cover"}}/>
          : <div style={{width: "100%", height: "100%", backgroundColor: bg.color}}/>}
      </ButtonBase></Grid>)}
    </Grid>
  </StyledBoxColumn>
}

export type MainFragmentProps = BaseFragmentProps & {
  puppetIndex: number,
}

type MainFragmentState = BaseFragmentState & {
  previewOn?: boolean,
  flip: boolean,
  aspectRatio: number,
  background: Background,
}

export class MainFragment extends BaseFragment<MainFragmentProps, MainFragmentState> {

  protected onCreateState(): MainFragmentState {
    return {
      ...super.onCreateState(),
      flip: false,
      aspectRatio: 2,
      background: BACKGROUNDS[BACKGROUNDS.length - 1],
    };
  }

  componentDidUpdate(prevProps: Readonly<MainFragmentProps>, prevState: Readonly<MainFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.previewOn !== this.state.previewOn) {
      this.maybeBindPage();
    }
  }

  private async maybeBindPage() {
    if (this.state.previewOn) {
      await bindPage(this.props.puppetIndex);
    }
  }

  private onFlip() {
    this.setState({
      flip: !this.state.flip,
    });
  }

  private onAspectRatio(event) {
    App.CONTEXT.showActionsListPopover(event.target, null, Object.getOwnPropertyNames(AspectRatio).map(name => {
      const metadata = ASPECT_RATIO_METADATAS.findMetadata(AspectRatio[name]);
      return new Action(metadata.text, () => this.setState({aspectRatio: metadata.value}));
    }));
  }

  private onBackground(event) {
    App.CONTEXT.showPopover(event.target, null, () =>
      <BackgroundView
        initialBackground={this.state.background}
        onChange={(background) => this.setState({background})}/>);
  }

  private renderPreview(): ReactElement {
    return <StyledOrientationComponent breakpoint={"md"} render={orientation =>
      <>
        <div style={{
          width: orientation === Orientation.LANDSCAPE ? "100%" : null,
          maxWidth: PAGE_FRAGMENT_LG_WIDTH,
          height: orientation !== Orientation.LANDSCAPE ? "100%" : null,
          maxHeight: PAGE_FRAGMENT_LG_WIDTH,
          // height: "100%",
          aspectRatio: this.state.aspectRatio,
          // maxWidth: 1280,
          borderRadius: BORDER_RADIUS,
          background: nightGray,
          position: "relative",
        }}>
          {this.state.background?.type === BackgroundType.IMAGE
            ? <img src={this.state.background.imageUrl} style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: BORDER_RADIUS,
              objectFit: "cover"
            }}/>
            : <div style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundColor: this.state.background?.color || nightGray,
              borderRadius: BORDER_RADIUS
            }}/>}
          {this.state.previewOn
            ? <Box style={{display: "absolute", width: "100%", height: "100%"}}>
              <div id="info" style={{display: "none"}}>
              </div>
              <div id="loading" style={{alignSelf: "center", justifySelf: "center"}}>
                <CircularProgress/>
                {/*<span className="spinner-text" id="status">*/}
                {/*    Loading model...*/}
                {/*</span>*/}
                {/*<div className="sk-spinner sk-spinner-pulse"></div>*/}
              </div>
              <div style={{
                alignSelf: "center",
                width: "100%",
                aspectRatio: this.state.aspectRatio,
                display: "flex",
                justifyContent: "center",
                position: "relative",
              }}>
                <div id='main' style={{
                  display: "none",
                  left: 0,
                  top: 0,
                  position: "absolute",
                }}>
                  <video id="video" playsInline style={{
                    transform: "scaleX(-1)",
                    display: "none",
                  }}>
                  </video>
                  <canvas id="output" style={{
                    position: "absolute",
                    transform: "scale(0.85, 0.85)",
                    transformOrigin: "0 0",
                    left: 10,
                    top: 10,
                  }}/>
                  <canvas id="keypoints" style={{
                    position: "absolute",
                    transform: "scale(0.85, 0.85)",
                    transformOrigin: "0 0",
                    left: 10,
                    top: 10,
                  }}/>
                </div>
                <canvas className="illustration-canvas"/>
              </div>
            </Box>
            : null}
        </div>
      </>}/>
  }

  renderContainerContent(): ReactElement {
    return <div style={{width: "100vw", display: "flex", flexDirection: "column", flexGrow: 1}}>
      <div style={{
        position: "relative",
        display: "flex",
        flexGrow: 1,
        padding: "4%"
      }}>
        <StyledBoxRow style={{
          position: "absolute",
          background: "white",
          borderRadius: 24,
          border: DIVIDER,
          zIndex: 1,
          top: PD_MD,
          right: PD_MD
        }}>
          {/*<Tooltip title={"Flip"}>*/}
          {/*  <IconButton onClick={() => this.onFlip()}>*/}
          {/*    <FlipOutlined/>*/}
          {/*  </IconButton>*/}
          {/*</Tooltip>*/}
          <Tooltip title={"Aspect Ratio"}>
            <IconButton onClick={(event) => this.onAspectRatio(event)}>
              <AspectRatioOutlined/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Background"}>
            <IconButton onClick={(event) => this.onBackground(event)}>
              <ColorLensOutlined/>
            </IconButton>
          </Tooltip>
        </StyledBoxRow>
        <Box
          style={{
            width: "100%",
            display: "flex",
            position: "relative",
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "center"
          }}>
          {this.renderPreview()}
        </Box>
      </div>
      <StyledBoxRow style={{justifyContent: "center", flexShrink: 0, padding: 16, borderTop: DIVIDER}}>
        <Button
          onClick={() => this.setState({previewOn: !this.state.previewOn})}>{this.state.previewOn ? "Preview off" : "Preview on"}</Button>
        <Button variant="contained">Record face and voice</Button>
      </StyledBoxRow>
    </div>;
  }
}
